import { useEffect } from "react";
import { cartExtension, grantedTotal } from "./cart-extension";
import { Slot } from "../slots/Slot";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import type { UseCartResult } from "./useCart";

/**
 * The phone cart.
 *
 * On a phone the cart is NOT a pane. A permanent side-by-side split leaves the
 * menu about 300px tall, which is roughly four items — so ordering becomes
 * scrolling, and scrolling during a rush is what makes staff give up on a
 * mobile till.
 *
 * Instead: the menu owns the screen, and the cart collapses to a bar showing
 * the two things that matter mid-order — how many items, and the total. Tapping
 * it opens the full cart. The pay button stays permanently reachable under the
 * thumb, because it is the most-pressed control in the app.
 */

interface CartBarProps {
  cart: UseCartResult;
  currency: CurrencyFormat;
  showTax: boolean;
  expanded: boolean;
  onToggle(): void;
  onTender(): void;
  onDiscount(): void;
}

export function CartBar({
  cart,
  currency,
  showTax,
  expanded,
  onToggle,
  onTender,
  onDiscount,
}: CartBarProps) {
  const { lines, priced } = cart;
  const itemCount = lines.reduce((total, line) => total + line.quantity, 0);

  // An empty cart has nothing to show, so collapse rather than leaving an empty
  // sheet covering the menu.
  useEffect(() => {
    if (cart.isEmpty && expanded) onToggle();
  }, [cart.isEmpty, expanded, onToggle]);

  return (
    <>
      {expanded && <div className="cartbar__scrim" onClick={onToggle} aria-hidden="true" />}

      <aside className={`cartbar${expanded ? " is-expanded" : ""}`}>
        <button
          type="button"
          className="cartbar__summary"
          onClick={onToggle}
          disabled={cart.isEmpty}
          aria-expanded={expanded}
        >
          <span className="cartbar__grip" aria-hidden="true" />
          <span className="cartbar__count">
            {itemCount === 0 ? "No items" : `${itemCount} ${itemCount === 1 ? "item" : "items"}`}
          </span>
          <span className="cartbar__total money">{formatMoney(priced.total, currency)}</span>
        </button>

        {expanded && (
          <div className="cartbar__lines">
            {priced.lines.map((line) => {
              const source = lines.find((l) => l.lineId === line.lineId)!;
              return (
                <article key={line.lineId} className="cart__line">
                  <div className="cart__line-main">
                    <span className="cart__line-name">
                      {line.itemName}
                      {line.variantName && (
                        <span className="cart__line-variant"> {line.variantName}</span>
                      )}
                    </span>
                    <span className="cart__line-total money">
                      {formatMoney(line.lineTotal, currency)}
                    </span>
                  </div>

                  {line.modifiers.length > 0 && (
                    <p className="cart__line-mods">
                      {line.modifiers.map((m) => m.name).join(", ")}
                    </p>
                  )}
                  {source.note && <p className="cart__line-note">{source.note}</p>}
                  {cart.grants[line.lineId] && (
                    <p className="cart__line-grant">{cart.grants[line.lineId]!.label}</p>
                  )}

                  <div className="cart__line-controls">
                    <button
                      type="button"
                      className="cart__step"
                      onClick={() => cart.decrement(line.lineId)}
                      aria-label={`Reduce ${line.itemName}`}
                    >
                      −
                    </button>
                    <span className="cart__qty money">{line.quantity}</span>
                    <button
                      type="button"
                      className="cart__step"
                      onClick={() => cart.increment(line.lineId)}
                      aria-label={`Add another ${line.itemName}`}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="cart__remove"
                      onClick={() => cart.remove(line.lineId)}
                      aria-label={`Remove ${line.itemName}`}
                    >
                      Remove
                    </button>
                  </div>
                </article>
              );
            })}

            <div className="cartbar__totals">
              <Slot id="till.cart.footer" cart={cartExtension(cart)} />
              <div className="cart__row">
                <span>Subtotal</span>
                <span className="money">{formatMoney(priced.subtotal, currency)}</span>
              </div>
              {grantedTotal(cart) > 0 && (
                <div className="cart__row">
                  <span>Free items</span>
                  <span className="money">−{formatMoney(grantedTotal(cart), currency)}</span>
                </div>
              )}
              {priced.discountTotal - grantedTotal(cart) > 0 && (
                <div className="cart__row">
                  <span>Discount{cart.promo ? ` ${cart.promo.rateBp / 100}%` : cart.statutory ? " (Senior/PWD)" : ""}</span>
                  <span className="money">
                    −{formatMoney((priced.discountTotal - grantedTotal(cart)) as typeof priced.total, currency)}
                  </span>
                </div>
              )}

              <button
                type="button"
                className="cart__discount"
                disabled={cart.isEmpty}
                onClick={onDiscount}
              >
                {cart.promo
                  ? `Discount ${cart.promo.rateBp / 100}% applied`
                  : cart.statutory
                    ? "Senior/PWD discount applied"
                    : "Discount"}
              </button>
              {showTax && priced.taxAmount > 0 && (
                <div className="cart__row cart__row--muted">
                  <span>VAT included</span>
                  <span className="money">{formatMoney(priced.taxAmount, currency)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          className="cartbar__pay"
          disabled={cart.isEmpty}
          onClick={onTender}
        >
          Take payment
        </button>
      </aside>
    </>
  );
}

export default CartBar;
